/**
 * Platform Admin Skill Detail
 * tabs: 概览 / 版本 / Skill 内容 / 部署 / 变更记录
 */
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Input, Modal, Select, Space, Table, Tabs, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  platformApproveImportReview,
  platformCreateSkillVersion,
  platformDeploySkill,
  platformGetSkill,
  platformGetSkillNodeBindings,
  platformOfflineSkill,
  platformPatchSkillVersion,
  platformPublishSkillOnline,
  platformRejectImportReview,
  platformRollbackSkill,
  type NodeSkillBindingRow,
} from '../api'
import type { SkillDetail, SkillVersionDetail } from '../types'
import { formatDateTime } from './adminShared'
import { platformColors } from './theme'
import './skill-library.css'

const TRUST_TONE: Record<string, { color: string; label: string }> = {
  OFFICIAL: { color: 'green', label: 'OFFICIAL' },
  REVIEWED: { color: 'blue', label: 'REVIEWED' },
  UNTRUSTED: { color: 'orange', label: 'UNTRUSTED' },
}

const REVIEW_TONE: Record<string, { color: string; label: string }> = {
  NONE: { color: 'default', label: 'NONE' },
  PENDING: { color: 'gold', label: 'PENDING' },
  APPROVED: { color: 'green', label: 'APPROVED' },
  REJECTED: { color: 'red', label: 'REJECTED' },
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  const text = value == null ? '' : typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  return (
    <div className="skill-json-block">
      <div className="skill-json-title">{title}</div>
      <pre>{text || '—'}</pre>
    </div>
  )
}

export default function SkillDetailPage() {
  const { skillId = '' } = useParams()
  const [detail, setDetail] = useState<SkillDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null)
  const [contentDraft, setContentDraft] = useState<{ methodology: string; instructions: string }>({
    methodology: '',
    instructions: '',
  })
  const [saving, setSaving] = useState(false)
  const [nodeBindings, setNodeBindings] = useState<NodeSkillBindingRow[]>([])
  const [nodeBindingsLoading, setNodeBindingsLoading] = useState(false)
  const [reviewing, setReviewing] = useState(false)

  const loadNodeBindings = useCallback(() => {
    if (!skillId) return
    setNodeBindingsLoading(true)
    platformGetSkillNodeBindings(skillId)
      .then((res) => setNodeBindings(res.data || []))
      .catch(() => setNodeBindings([]))
      .finally(() => setNodeBindingsLoading(false))
  }, [skillId])

  useEffect(() => {
    loadNodeBindings()
  }, [loadNodeBindings])

  const load = useCallback(() => {
    if (!skillId) return
    setLoading(true)
    platformGetSkill(skillId)
      .then((res) => {
        setDetail(res.data)
        const preferred =
          res.data.current_production_version_id ||
          res.data.versions[0]?.id ||
          null
        setSelectedVersionId(preferred)
        const ver = res.data.versions.find((v) => v.id === preferred) || res.data.versions[0]
        setContentDraft({
          methodology: ver?.methodology || '',
          instructions: ver?.instructions || '',
        })
      })
      .catch((err) => message.error(err?.response?.data?.detail?.message || '加载失败'))
      .finally(() => setLoading(false))
  }, [skillId])

  useEffect(() => {
    load()
  }, [load])

  const selected: SkillVersionDetail | undefined = detail?.versions.find((v) => v.id === selectedVersionId)

  useEffect(() => {
    if (!selected) return
    setContentDraft({
      methodology: selected.methodology || '',
      instructions: selected.instructions || '',
    })
  }, [selected?.id])

  const onCreateVersion = async () => {
    if (!detail) return
    try {
      const res = await platformCreateSkillVersion(detail.id, {
        base_version_id: selectedVersionId || undefined,
        change_summary: `Cloned from ${selected?.version || 'previous'}`,
      })
      message.success(`已创建 ${res.data.version}`)
      load()
      setSelectedVersionId(res.data.id)
    } catch (err: any) {
      message.error(err?.response?.data?.detail?.message || '创建版本失败')
    }
  }

  const onSaveDraft = async () => {
    if (!detail || !selected) return
    if (selected.status === 'PRODUCTION' || selected.status === 'DEPRECATED' || selected.status === 'ARCHIVED') {
      message.warning('生产/历史版本不可原地修改，请创建新版本')
      return
    }
    setSaving(true)
    try {
      await platformPatchSkillVersion(detail.id, selected.id, {
        methodology: contentDraft.methodology,
        instructions: contentDraft.instructions,
        input_schema: selected.input_schema,
        output_schema: selected.output_schema,
        context_policy: selected.context_policy,
        model_policy: selected.model_policy,
        tool_policy: selected.tool_policy,
        quality_rubric: selected.quality_rubric,
        retry_policy: selected.retry_policy,
      })
      message.success('草稿已保存')
      load()
    } catch (err: any) {
      message.error(err?.response?.data?.detail?.message || '保存失败')
    } finally {
      setSaving(false)
    }
  }

  const onDeploy = async (target: string) => {
    if (!detail || !selected) return
    Modal.confirm({
      title: `确认部署为 ${target}？`,
      content: `${selected.version} → ${target}（将写入变更记录）`,
      onOk: async () => {
        try {
          await platformDeploySkill(detail.id, {
            skill_version_id: selected.id,
            target_status: target,
            traffic_weight: target === 'DISABLED' ? 0 : 100,
          })
          message.success('部署完成')
          load()
        } catch (err: any) {
          message.error(err?.response?.data?.detail?.message || '部署失败')
        }
      },
    })
  }

  const onRollback = async (versionId: string) => {
    if (!detail) return
    Modal.confirm({
      title: '确认回滚到该历史版本？',
      content: '不会删除当前版本，仅切换生产部署并记录变更。',
      onOk: async () => {
        try {
          await platformRollbackSkill(detail.id, { skill_version_id: versionId })
          message.success('已回滚')
          load()
        } catch (err: any) {
          message.error(err?.response?.data?.detail?.message || '回滚失败')
        }
      },
    })
  }

  // VISTOERA_SKILL_PACKAGE_REGISTRY_..._P0_V1 §9/§11 — admin review gate for
  // imported packages. UNTRUSTED -> REVIEWED is the only path that makes a
  // version promotion-eligible; IMPORT_AUTO_PRODUCTION is never possible.
  const onApproveImport = async (versionId: string) => {
    if (!detail) return
    setReviewing(true)
    try {
      await platformApproveImportReview(detail.id, versionId)
      message.success('审核通过：该版本已可提升为 Production')
      load()
    } catch (err: any) {
      message.error(err?.response?.data?.detail?.message || '审核操作失败')
    } finally {
      setReviewing(false)
    }
  }

  const onRejectImport = async (versionId: string) => {
    if (!detail) return
    Modal.confirm({
      title: '确认拒绝该导入版本？',
      content: '拒绝后该版本仍保持 UNTRUSTED，无法提升为 Production。',
      onOk: async () => {
        setReviewing(true)
        try {
          await platformRejectImportReview(detail.id, versionId)
          message.success('已拒绝该导入版本')
          load()
        } catch (err: any) {
          message.error(err?.response?.data?.detail?.message || '审核操作失败')
        } finally {
          setReviewing(false)
        }
      },
    })
  }

  if (!detail && loading) {
    return <div className="skill-library-page">加载中…</div>
  }
  if (!detail) {
    return (
      <div className="skill-library-page">
        <Link to="/platform/skills">返回 Skill 库</Link>
        <p>Skill 不存在</p>
      </div>
    )
  }

  return (
    <div className="skill-library-page">
      <div className="skill-library-header">
        <div>
          <Link to="/platform/skills" className="skill-back">
            <ArrowLeftOutlined /> 返回 Skill 库
          </Link>
          <div className="skill-breadcrumb">平台管理 / Skill 库 / {detail.display_name}</div>
          <h1 className="skill-title">{detail.display_name}</h1>
          <p className="skill-subtitle">
            <code>{detail.skill_key}</code> · {detail.domain_label} · {detail.stage_label || '未绑定阶段'}
          </p>
        </div>
        <Space wrap>
          <Button icon={<PlusOutlined />} onClick={onCreateVersion}>
            创建新版本
          </Button>
          <Button onClick={() => onDeploy('TESTING')}>部署到 Testing</Button>
          <Button type="primary" onClick={() => onDeploy('PRODUCTION')}>
            提升到 Production
          </Button>
          {detail.status === 'DISABLED' || detail.status === 'DRAFT' ? (
            <Button
              onClick={() => {
                void (async () => {
                  try {
                    await platformPublishSkillOnline(detail.id)
                    message.success('Skill 已上线')
                    load()
                  } catch (err: any) {
                    message.error(err?.response?.data?.detail?.message || '上线失败')
                  }
                })()
              }}
            >
              上线
            </Button>
          ) : (
            <Button
              danger
              onClick={() => {
                Modal.confirm({
                  title: '确认下线该 Skill？',
                  content: '下线后将清除各功能节点上的默认标记；历史版本与绑定关系保留。',
                  onOk: async () => {
                    await platformOfflineSkill(detail.id)
                    message.success('Skill 已下线')
                    load()
                  },
                })
              }}
            >
              下线
            </Button>
          )}
        </Space>
      </div>

      <Tabs
        items={[
          {
            key: 'overview',
            label: '概览',
            children: (
              <div className="skill-detail-grid">
                <div className="skill-panel">
                  <h3>基本信息</h3>
                  <dl className="skill-dl">
                    <dt>状态</dt>
                    <dd>{detail.status}</dd>
                    <dt>当前生产版本</dt>
                    <dd>{detail.current_version || '—'}</dd>
                    <dt>生产流量</dt>
                    <dd>{detail.production_traffic}</dd>
                    <dt>总执行</dt>
                    <dd>{detail.total_executions}</dd>
                    <dt>成功率</dt>
                    <dd>{detail.success_rate == null ? '—' : `${detail.success_rate}%`}</dd>
                    <dt>描述</dt>
                    <dd>{detail.description || '—'}</dd>
                  </dl>
                </div>
                <div className="skill-panel">
                  <h3>Stage Binding</h3>
                  {(detail.stage_bindings || []).length === 0 ? (
                    <div style={{ color: platformColors.mutedText }}>暂无绑定</div>
                  ) : (
                    detail.stage_bindings.map((b) => (
                      <div key={b.id} style={{ marginBottom: 8 }}>
                        {b.stage_label || b.stage_key} · priority {b.priority}{' '}
                        {b.enabled ? <Tag color="success">enabled</Tag> : <Tag>disabled</Tag>}
                      </div>
                    ))
                  )}
                </div>
                <div className="skill-panel">
                  <h3>数据表现</h3>
                  <div style={{ color: platformColors.mutedText }}>
                    {detail.performance?.message || '暂无发布表现数据'}
                  </div>
                </div>
                <div className="skill-panel">
                  <h3>最近执行</h3>
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={detail.recent_executions || []}
                    columns={[
                      { title: '状态', dataIndex: 'status' },
                      { title: 'Provider', dataIndex: 'provider', render: (v) => v || '—' },
                      { title: 'Model', dataIndex: 'model', render: (v) => v || '—' },
                      { title: '时间', dataIndex: 'started_at', render: (v) => formatDateTime(v) },
                    ]}
                    locale={{ emptyText: '暂无执行记录' }}
                  />
                </div>
              </div>
            ),
          },
          {
            key: 'versions',
            label: '版本',
            children: (
              <Table
                rowKey="id"
                dataSource={detail.versions}
                pagination={false}
                columns={[
                  {
                    title: '版本',
                    dataIndex: 'version',
                    render: (v: string, row: SkillVersionDetail) => (
                      <Button type="link" onClick={() => setSelectedVersionId(row.id)}>
                        {v}
                        {row.id === detail.current_production_version_id ? (
                          <Tag color="success" style={{ marginLeft: 6 }}>
                            Production
                          </Tag>
                        ) : null}
                      </Button>
                    ),
                  },
                  { title: '状态', dataIndex: 'status' },
                  { title: '来源', dataIndex: 'source_type' },
                  { title: '说明', dataIndex: 'change_summary', render: (v) => v || '—' },
                  { title: '创建时间', dataIndex: 'created_at', render: (v) => formatDateTime(v) },
                  {
                    title: '操作',
                    key: 'actions',
                    render: (_: unknown, row: SkillVersionDetail) => (
                      <Button size="small" onClick={() => onRollback(row.id)}>
                        回滚为生产
                      </Button>
                    ),
                  },
                ]}
              />
            ),
          },
          {
            key: 'content',
            label: 'Skill 内容',
            children: selected ? (
              <div className="skill-content-editor">
                <div style={{ marginBottom: 12 }}>
                  编辑版本：
                  <Select
                    style={{ width: 180, marginLeft: 8 }}
                    value={selected.id}
                    onChange={setSelectedVersionId}
                    options={detail.versions.map((v) => ({
                      value: v.id,
                      label: `${v.version} (${v.status})`,
                    }))}
                  />
                  <Tag style={{ marginLeft: 8 }}>{selected.status}</Tag>
                  {selected.source_reference ? (
                    <Tag color="blue">source: {selected.source_reference}</Tag>
                  ) : null}
                  <Tag color={TRUST_TONE[selected.trust_level]?.color || 'default'}>
                    {TRUST_TONE[selected.trust_level]?.label || selected.trust_level}
                  </Tag>
                </div>
                {selected.trust_level === 'UNTRUSTED' ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="该版本来自 Skill Package 导入，处于 UNTRUSTED 状态"
                    description="在管理员于「来源」页签审核通过前，此版本无法提升为 Production（IMPORT_AUTO_PRODUCTION=NO）。"
                  />
                ) : null}
                <label className="skill-field">
                  Methodology
                  <Input.TextArea
                    rows={4}
                    value={contentDraft.methodology}
                    onChange={(e) => setContentDraft((d) => ({ ...d, methodology: e.target.value }))}
                    disabled={selected.status === 'PRODUCTION'}
                  />
                </label>
                <label className="skill-field">
                  Instructions
                  <Input.TextArea
                    rows={10}
                    value={contentDraft.instructions}
                    onChange={(e) => setContentDraft((d) => ({ ...d, instructions: e.target.value }))}
                    disabled={selected.status === 'PRODUCTION' || selected.source_type === 'FILESYSTEM_HARNESS'}
                  />
                </label>
                <div className="skill-json-grid">
                  <JsonBlock title="Input Schema" value={selected.input_schema} />
                  <JsonBlock title="Output Schema" value={selected.output_schema} />
                  <JsonBlock title="Context Policy" value={selected.context_policy} />
                  <JsonBlock title="Model Policy" value={selected.model_policy} />
                  <JsonBlock title="Tool Policy" value={selected.tool_policy} />
                  <JsonBlock title="Quality Rubric" value={selected.quality_rubric} />
                  <JsonBlock title="Retry Policy" value={selected.retry_policy} />
                </div>
                <div style={{ marginTop: 12 }}>
                  <Button type="primary" loading={saving} onClick={onSaveDraft}>
                    保存草稿
                  </Button>
                  {selected.source_license ? (
                    <span style={{ marginLeft: 12, color: platformColors.mutedText }}>
                      License: {selected.source_license}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : (
              <div>暂无版本</div>
            ),
          },
          {
            key: 'deploy',
            label: '部署',
            children: (
              <Table
                rowKey="id"
                dataSource={detail.deployments}
                pagination={false}
                columns={[
                  { title: '环境', dataIndex: 'environment' },
                  { title: '状态', dataIndex: 'deployment_status' },
                  { title: '流量', dataIndex: 'traffic_weight', render: (n) => `${n}%` },
                  {
                    title: 'Active',
                    dataIndex: 'active',
                    render: (v: boolean) => (v ? <Tag color="success">active</Tag> : <Tag>ended</Tag>),
                  },
                  { title: '开始', dataIndex: 'started_at', render: (v) => formatDateTime(v) },
                  { title: '结束', dataIndex: 'ended_at', render: (v) => formatDateTime(v) },
                ]}
              />
            ),
          },
          {
            key: 'changelog',
            label: '变更记录',
            children: (
              <Table
                rowKey="id"
                dataSource={detail.change_log}
                pagination={false}
                columns={[
                  { title: '动作', dataIndex: 'action' },
                  { title: '从', dataIndex: 'from_version', render: (v) => v || '—' },
                  { title: '到', dataIndex: 'to_version', render: (v) => v || '—' },
                  { title: '说明', dataIndex: 'summary' },
                  { title: '时间', dataIndex: 'created_at', render: (v) => formatDateTime(v) },
                ]}
              />
            ),
          },
          {
            key: 'node-bindings',
            label: '节点绑定',
            children: (
              <div>
                <Typography.Paragraph type="secondary">
                  该 Skill 当前绑定到的功能节点 Capability（一个 Skill 可绑定多个兼容节点）。绑定关系仅存于
                  FunctionNodeSkillBinding，本页从不复制 SkillDefinition。
                </Typography.Paragraph>
                <Table
                  rowKey="id"
                  loading={nodeBindingsLoading}
                  dataSource={nodeBindings}
                  pagination={false}
                  columns={[
                    { title: 'Capability', dataIndex: 'capability' },
                    { title: 'Canvas Node', dataIndex: 'canvas_node_type', render: (v) => v || '—' },
                    {
                      title: '启用',
                      dataIndex: 'enabled',
                      render: (v: boolean) => (v ? <Tag color="success">enabled</Tag> : <Tag>disabled</Tag>),
                    },
                    {
                      title: '默认',
                      dataIndex: 'is_default',
                      render: (v: boolean) => (v ? <Tag color="blue">default</Tag> : '—'),
                    },
                    { title: '解析版本', dataIndex: 'version', render: (v) => v || '—' },
                  ]}
                  locale={{ emptyText: '该 Skill 尚未绑定任何功能节点' }}
                />
              </div>
            ),
          },
          {
            key: 'source',
            label: '来源',
            children: selected ? (
              <div className="skill-detail-grid">
                <div className="skill-panel">
                  <h3>来源 / Provenance</h3>
                  <dl className="skill-dl">
                    <dt>来源类型</dt>
                    <dd>{selected.source_type}</dd>
                    <dt>信任等级</dt>
                    <dd>
                      <Tag color={TRUST_TONE[selected.trust_level]?.color || 'default'}>
                        {TRUST_TONE[selected.trust_level]?.label || selected.trust_level}
                      </Tag>
                    </dd>
                    <dt>审核状态</dt>
                    <dd>
                      <Tag color={REVIEW_TONE[selected.review_status]?.color || 'default'}>
                        {REVIEW_TONE[selected.review_status]?.label || selected.review_status}
                      </Tag>
                    </dd>
                    <dt>来源名称</dt>
                    <dd>{selected.source_name || '—'}</dd>
                    <dt>来源 URL</dt>
                    <dd>{selected.source_url || '—'}</dd>
                    <dt>来源版本</dt>
                    <dd>{selected.source_version || '—'}</dd>
                    <dt>Upstream Commit</dt>
                    <dd>{selected.upstream_commit_sha || '—'}</dd>
                    <dt>License</dt>
                    <dd>{selected.source_license || '—'}</dd>
                    <dt>导入时间</dt>
                    <dd>{selected.imported_at ? formatDateTime(selected.imported_at) : '—'}</dd>
                    <dt>Fork 自版本</dt>
                    <dd>{selected.derived_from_skill_version_id || '—'}</dd>
                  </dl>
                  {selected.trust_level === 'UNTRUSTED' ? (
                    <Space style={{ marginTop: 12 }}>
                      <Button type="primary" loading={reviewing} onClick={() => onApproveImport(selected.id)}>
                        审核通过（允许提升为 Production）
                      </Button>
                      <Button danger loading={reviewing} onClick={() => onRejectImport(selected.id)}>
                        拒绝
                      </Button>
                    </Space>
                  ) : (
                    <Typography.Text type="secondary">
                      非 UNTRUSTED 版本无需审核（Vistoera 官方 / 内部创建 / 已审核通过）。
                    </Typography.Text>
                  )}
                </div>
                <div className="skill-panel">
                  <JsonBlock title="Package Manifest (skill.yaml 快照)" value={selected.package_manifest} />
                </div>
                {selected.evals ? (
                  <div className="skill-panel">
                    <JsonBlock title="Evals (预留)" value={selected.evals} />
                  </div>
                ) : null}
              </div>
            ) : (
              <div>暂无版本</div>
            ),
          },
        ]}
      />
    </div>
  )
}
